import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Building2, ShieldCheck, Scale, BadgeIndianRupee, FileText, Sparkles, Award, ArrowRight, Gavel, Clock, IndianRupee, KeyRound, Flame, PlusCircle, Star, Quote, ChevronDown, Crown, BookOpen, BedDouble, Bath, Maximize } from "lucide-react";
import { api, formatINR, fileUrl } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";
import AgentCard from "../components/AgentCard";
import InstallPWAButton from "../components/InstallPWAButton";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import { GUIDES } from "./InvestmentGuides";
import MobileNativeHome from "../components/MobileNativeHome";
import ServiceProfessionalsCarousel from "../components/ServiceProfessionalsCarousel";

const HERO_BG = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400";

/** 99acres-style top tabs. `v` is used to key state, `to` is external route override. */
const HERO_TABS = [
    { v: "sale-residential",    label: "Buy",             listing: "sale", type: "residential" },
    { v: "rent-residential",    label: "Rent",            listing: "rent", type: "residential" },
    { v: "new-launch",          label: "New Launch",      to: "/new-projects" },
    { v: "pg",                  label: "PG / Co-Living",  listing: "pg" },
    { v: "commercial-buy",      label: "Commercial Buy",  listing: "sale", type: "commercial" },
    { v: "commercial-rent",     label: "Commercial Rent", listing: "rent", type: "commercial" },
    { v: "plots",               label: "Plots / Land",    listing: "sale", type: "plot" },
    { v: "projects",            label: "Projects",        listing: "sale", type: "residential", featured: true, to: "/properties?featured=true" },
    { v: "sell",                label: "Post FREE",       external: true, to: "/add-property" },
];

const SERVICES = [
    { icon: Building2, title: "Property Marketplace", desc: "Buy, sell, rent residential, commercial, plots & farmland — all in one place.", to: "/properties" },
    { icon: Gavel, title: "Online Auctions", desc: "Live bidding on bank-attached, SARFAESI, and court-ordered auction properties.", to: "/auctions" },
    { icon: ShieldCheck, title: "Verified Brokers", desc: "RERA-certified brokers with KYC verification, ratings, and proven track record.", to: "/brokers" },
    { icon: Scale, title: "Legal Verification", desc: "Title checks, agreement drafting, registry support by verified property advocates.", to: "/advocates" },
    { icon: BadgeIndianRupee, title: "Home Loans", desc: "Compare rates from 20+ banks. EMI calculator, eligibility checker, expert agents.", to: "/loan-services" },
    { icon: FileText, title: "Documentation", desc: "Stamp duty, registry, tax consultation. Digital tracking, end-to-end support.", to: "/legal-services" },
];

const STATS = [
    { value: "12K+", label: "Verified Listings" },
    { value: "1,200+", label: "RERA Brokers" },
    { value: "320+", label: "Advocates & Agents" },
    { value: "50+", label: "Cities" },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [luxury, setLuxury] = useState([]);
    const [partners, setPartners] = useState([]);
    const [upcomingAuctions, setUpcomingAuctions] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [openFaq, setOpenFaq] = useState(0);
    const [search, setSearch] = useState({ q: "", tab: "sale-residential" });
    const [heroSuggestions, setHeroSuggestions] = useState([]);
    const [heroSuggestionsOpen, setHeroSuggestionsOpen] = useState(false);
    const [heroBgIdx, setHeroBgIdx] = useState(0);
    const navigate = useNavigate();
    const { items: recentlyViewed } = useRecentlyViewed();

    // Pool of images for the hero background carousel — prefers real premium property photos, falls back to stock skyline
    const heroBackdrops = useMemo(() => {
        const fromProps = luxury.length ? luxury : featured;
        const imgs = fromProps
            .map((p) => p.images?.[0])
            .filter(Boolean)
            .slice(0, 8)
            .map((src) => fileUrl(src));
        // Always keep at least the stock skyline as a fallback / first frame
        return imgs.length ? imgs : [HERO_BG];
    }, [luxury, featured]);

    // Auto-rotate hero background every 4.5s
    useEffect(() => {
        if (heroBackdrops.length <= 1) return;
        const t = setInterval(() => setHeroBgIdx((i) => (i + 1) % heroBackdrops.length), 4500);
        return () => clearInterval(t);
    }, [heroBackdrops.length]);

    useEffect(() => {
        // Fetch all active properties for the homepage strip (latest first).
        api.get("/properties", { params: { limit: 60 } })
            .then((r) => setFeatured(r.data.items || []))
            .catch(() => {});
        // Luxury strip — properties priced at ₹1 Cr or more (rent ≥ ₹50K/mo). Descending by price.
        api.get("/properties", { params: { min_price: 10000000, listing_type: "sale", limit: 12 } })
            .then((r) => {
                const items = (r.data.items || []).sort((a, b) => (b.price || 0) - (a.price || 0));
                setLuxury(items);
            })
            .catch(() => {});
        api.get("/partners", { params: { verified: true } })
            .then((r) => setPartners((r.data || []).slice(0, 4)))
            .catch(() => {});
        api.get("/testimonials", { params: { limit: 8 } })
            .then((r) => setTestimonials(r.data?.items || []))
            .catch(() => {});
        // Upcoming + live bank auctions for homepage strip
        Promise.all([
            api.get("/auctions", { params: { status: "upcoming" } }).catch(() => ({ data: [] })),
            api.get("/auctions", { params: { status: "live" } }).catch(() => ({ data: [] })),
        ]).then(([up, live]) => {
            const combined = [...(live.data || []), ...(up.data || [])].slice(0, 8);
            setUpcomingAuctions(combined);
        });
    }, []);

    // Hero locality/city autocomplete — debounced
    useEffect(() => {
        const q = (search.q || "").trim();
        if (q.length < 1) {
            setHeroSuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/properties/localities", { params: { q, limit: 8 } });
                setHeroSuggestions(data.items || []);
            } catch {
                setHeroSuggestions([]);
            }
        }, 220);
        return () => clearTimeout(timer);
    }, [search.q]);

    // Map every hero tab to a full /properties filter URL — this powers the search button too.
    const buildTabUrl = (tab, extraQ) => {
        const t = HERO_TABS.find((x) => x.v === tab);
        if (!t) return "/properties";
        if (t.external) return t.to;
        if (t.to && !t.listing) return t.to;  // Explicit route override (e.g. New Launch → /new-projects)
        const p = new URLSearchParams();
        if (t.listing) p.set("listing", t.listing);
        if (t.type) p.set("type", t.type);
        if (t.featured) p.set("featured", "true");
        const q = (extraQ ?? search.q).trim();
        if (q) {
            const parts = q.split(",").map((s) => s.trim()).filter(Boolean);
            if (parts.length >= 2) {
                p.set("city", parts[parts.length - 1]);
                p.set("locality", parts.slice(0, -1).join(", "));
            } else {
                p.set("city", q);
            }
        }
        return `/properties?${p.toString()}`;
    };

    const onSearch = (e) => {
        e.preventDefault();
        setHeroSuggestionsOpen(false);
        navigate(buildTabUrl(search.tab));
    };

    const pickSuggestion = (s) => {
        const q = `${s.locality}, ${s.city}`;
        setSearch((prev) => ({ ...prev, q }));
        setHeroSuggestionsOpen(false);
        navigate(buildTabUrl(search.tab, q));
    };

    return (
        <div className="min-h-screen">
            <Seo
                title="Buy, Sell & Rent Properties in Nagpur"
                description="Explore verified plots, flats, commercial and auction properties in Nagpur and Maharashtra with Global Property Mart. RERA-verified brokers, advocates & loan agents — one platform for every property need."
                path="/"
                keywords="property in Nagpur, plots in Nagpur, buy plot in Nagpur, residential plots in Nagpur, commercial property in Nagpur, real estate company in Nagpur, property dealers in Nagpur, plots in Maharashtra, real estate marketplace Maharashtra, NA plots Nagpur, auction properties Nagpur, gated community plots Nagpur, property near Wardha Road, plots near MIHAN, property near Nagpur Metro, plots near Samruddhi Mahamarg"
            />
            {/* MOBILE-ONLY (< md): Native app-styled 3-scroll Home Screen */}
            <MobileNativeHome properties={featured} />

            {/* DESKTOP + tablet (>= md): keep the classic 99acres-inspired hero + rich sections */}
            <div className="hidden md:block">

            {/* HERO — 99acres-inspired tabbed search (DESKTOP + tablet, hidden on mobile) */}
            <section className="hidden md:flex relative min-h-[92vh] items-center overflow-hidden hero-section" data-testid="hero-99acres">
                <div className="absolute inset-0">
                    {/* Auto-rotating premium property image carousel — crossfade between slides */}
                    {heroBackdrops.map((src, i) => (
                        <img
                            key={src + i}
                            src={src}
                            alt=""
                            aria-hidden="true"
                            className={`absolute inset-0 w-full h-full object-cover hero-image transition-opacity duration-[1500ms] ease-in-out ${i === heroBgIdx ? "opacity-100" : "opacity-0"}`}
                            loading={i === 0 ? "eager" : "lazy"}
                        />
                    ))}
                    {/* Stronger navy overlay so white text pops like 99acres */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#001a3d]/92 via-[#002657]/85 to-[#001a3d]/92" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#001a3d] via-transparent to-transparent opacity-60" />
                </div>
                <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />

                {/* Slide indicator dots (top-right) */}
                {heroBackdrops.length > 1 && (
                    <div className="absolute top-6 right-6 z-20 flex items-center gap-1.5" data-testid="hero-slide-dots">
                        {heroBackdrops.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Show slide ${i + 1}`}
                                onClick={() => setHeroBgIdx(i)}
                                className={`h-1.5 rounded-full transition-all ${i === heroBgIdx ? "w-6 bg-gold-400" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                            />
                        ))}
                    </div>
                )}

                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-20 sm:py-24 w-full">
                    <div className="text-on-image max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-gold-500/40 mb-5" data-testid="hero-trust-chip">
                            <ShieldCheck className="w-3.5 h-3.5 text-gold-400" />
                            <span className="text-[11px] font-bold text-gold-300 tracking-wider uppercase">100% Verified · RERA Certified</span>
                        </div>

                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-white leading-[1.05]">
                            Find a home you&apos;ll <span className="text-gold-400">love.</span>
                        </h1>
                        <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed mb-8">
                            Search from <strong className="text-gold-300 font-bold">verified listings</strong> across India — homes, plots, commercial spaces, PG and bank auctions. RERA-checked brokers, transparent pricing.
                        </p>

                        {/* SEARCH CARD */}
                        <div className="bg-white rounded-2xl shadow-[0_25px_70px_-15px_rgba(0,0,0,0.55)] border border-gold-500/40 overflow-visible" data-testid="hero-search">
                            {/* TABS — horizontal scroll on mobile */}
                            <div className="flex items-center gap-1 border-b border-navy-500/10 overflow-x-auto scrollbar-thin px-2 pt-2" role="tablist" data-testid="hero-tabs">
                                {HERO_TABS.map((t) => {
                                    const active = search.tab === t.v;
                                    if (t.external) {
                                        return (
                                            <Link
                                                key={t.v}
                                                to={t.to}
                                                data-testid={`hero-tab-${t.v}`}
                                                className="shrink-0 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold rounded-lg text-white bg-gradient-to-r from-gold-500 to-amber-500 shadow-md hover:brightness-110 whitespace-nowrap flex items-center gap-1.5 ml-1"
                                            >
                                                <PlusCircle className="w-3.5 h-3.5" />
                                                {t.label}
                                            </Link>
                                        );
                                    }
                                    return (
                                        <button
                                            key={t.v}
                                            type="button"
                                            role="tab"
                                            aria-selected={active}
                                            onClick={() => setSearch((s) => ({ ...s, tab: t.v }))}
                                            data-testid={`hero-tab-${t.v}`}
                                            className={`shrink-0 relative px-3 sm:px-4 py-3 text-xs sm:text-sm font-bold whitespace-nowrap transition-colors ${
                                                active
                                                    ? "text-gold-700"
                                                    : "text-navy-500 hover:text-gold-700"
                                            }`}
                                        >
                                            {t.label}
                                            {active && <span className="absolute inset-x-2 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-gold-500 to-amber-400" />}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* SEARCH INPUT ROW */}
                            <form onSubmit={onSearch} className="relative p-3 sm:p-4">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-stretch">
                                    <div className="md:col-span-10 relative">
                                        <div className="flex items-center bg-white border border-navy-500/15 rounded-xl focus-within:border-gold-500/60 focus-within:ring-2 focus-within:ring-gold-500/15 transition-all">
                                            <Search className="w-4 h-4 text-navy-500/50 shrink-0 ml-4" />
                                            <input
                                                type="text"
                                                data-testid="hero-search-input"
                                                value={search.q}
                                                onChange={(e) => { setSearch((s) => ({ ...s, q: e.target.value })); setHeroSuggestionsOpen(true); }}
                                                onFocus={() => setHeroSuggestionsOpen(true)}
                                                onBlur={() => setTimeout(() => setHeroSuggestionsOpen(false), 180)}
                                                placeholder="Search city, locality, project or society (e.g. Wardha Road, Nagpur)"
                                                className="w-full px-3 py-3.5 text-sm text-navy-500 placeholder:text-navy-500/40 bg-transparent focus:outline-none"
                                                autoComplete="off"
                                            />
                                            {search.q && (
                                                <button
                                                    type="button"
                                                    aria-label="Clear search"
                                                    onClick={() => setSearch((s) => ({ ...s, q: "" }))}
                                                    className="text-navy-500/40 hover:text-navy-500 pr-3 text-lg leading-none"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        {/* Autocomplete dropdown */}
                                        {heroSuggestionsOpen && heroSuggestions.length > 0 && (
                                            <div
                                                className="absolute left-0 right-0 top-full mt-1.5 max-h-72 overflow-y-auto rounded-xl bg-white border border-navy-500/15 shadow-2xl z-50"
                                                data-testid="hero-suggestions"
                                            >
                                                {heroSuggestions.map((s) => (
                                                    <button
                                                        key={`${s.locality}-${s.city}`}
                                                        type="button"
                                                        onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                                        className="w-full text-left px-4 py-3 hover:bg-gold-500/10 border-b border-navy-500/5 last:border-0 flex items-center justify-between gap-3"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <MapPin className="w-4 h-4 text-gold-600 shrink-0" />
                                                            <div>
                                                                <div className="text-navy-500 font-semibold text-sm">{s.locality}</div>
                                                                <div className="text-xs text-navy-500/55">{s.city} · Locality</div>
                                                            </div>
                                                        </div>
                                                        <span className="text-[10px] text-gold-700 font-bold px-2 py-0.5 rounded-full bg-gold-500/10 shrink-0">{s.count} listing{s.count === 1 ? "" : "s"}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="md:col-span-2">
                                        <button
                                            type="submit"
                                            data-testid="hero-search-btn"
                                            className="w-full h-full px-5 py-3.5 rounded-xl bg-gradient-to-r from-gold-500 to-amber-500 text-navy-900 font-extrabold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                                        >
                                            <Search className="w-4 h-4" /> Search
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* POPULAR CITIES — 99acres-style pills */}
                        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="hero-popular-cities">
                            <span className="text-[11px] text-white/60 font-semibold uppercase tracking-wider mr-1">Trending in India:</span>
                            {[
                                { c: "Nagpur", label: "Buy in Nagpur" },
                                { c: "Mumbai", label: "Buy in Mumbai" },
                                { c: "Pune", label: "Buy in Pune" },
                                { c: "Bengaluru", label: "Buy in Bengaluru" },
                                { c: "Delhi", label: "Rent in Delhi" },
                                { c: "Hyderabad", label: "Buy in Hyderabad" },
                            ].map((x) => (
                                <Link
                                    key={x.label}
                                    to={`/properties?city=${encodeURIComponent(x.c)}&listing=${x.label.startsWith("Rent") ? "rent" : "sale"}`}
                                    data-testid={`hero-trend-${x.c.toLowerCase()}`}
                                    className="text-on-image px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/25 text-xs font-medium text-white hover:bg-white/20 hover:border-gold-400/60 transition-colors"
                                >
                                    {x.label}
                                </Link>
                            ))}
                        </div>

                        {/* NAGPUR AREA SEO STRIP */}
                        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="popular-areas-strip">
                            <span className="text-[11px] text-white/60 font-semibold uppercase tracking-wider mr-1">Popular in Nagpur:</span>
                            {[
                                { slug: "wardha-road", label: "Wardha Road" },
                                { slug: "mihan", label: "MIHAN" },
                                { slug: "mohgaon", label: "Mohgaon" },
                                { slug: "beltarodi", label: "Beltarodi" },
                                { slug: "jamtha", label: "Jamtha" },
                            ].map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/property-in-nagpur/${a.slug}`}
                                    data-testid={`hero-area-${a.slug}`}
                                    className="text-on-image px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-[11px] font-medium text-white/85 hover:bg-white/10 hover:border-gold-400/50 transition-colors"
                                >
                                    {a.label}
                                </Link>
                            ))}
                            <Link
                                to="/property-in-nagpur"
                                data-testid="hero-areas-all"
                                className="text-on-image px-3 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/40 text-[11px] font-bold text-gold-300 hover:bg-gold-500/30 transition-colors"
                            >
                                View all areas →
                            </Link>
                        </div>

                        {/* Install PWA + Stats row */}
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <InstallPWAButton variant="hero" />
                        </div>

                        {/* STATS */}
                        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="hero-stats">
                            {STATS.map((s) => (
                                <div key={s.label} className="text-on-image rounded-xl bg-white/8 backdrop-blur-md border border-white/15 px-4 py-3">
                                    <div className="font-heading text-2xl sm:text-3xl font-bold text-white">{s.value}</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1 font-semibold">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ALL LISTINGS — Horizontal strip (shown immediately after hero) */}
            <section className="py-10 sm:py-20 bg-navy-950">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <div className="label-overline mb-3">All Listings</div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white">
                                Explore every <span className="text-gold-500">property</span>
                            </h2>
                            <p className="text-white/55 text-sm mt-3">Swipe to browse premium listings</p>
                        </div>
                        <Link
                            to="/properties"
                            data-testid="view-all-properties"
                            className="text-sm font-medium text-gold-500 hover:text-gold-100 inline-flex items-center gap-1 self-start md:self-auto"
                        >
                            Open full grid <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
                <div
                    className="px-5 sm:px-6 lg:px-8 overflow-x-auto pb-4 mobile-listing scrollbar-thin scrollbar-thumb-gold-500/40 scrollbar-track-transparent"
                    data-testid="home-properties-strip"
                >
                    <div className="flex gap-4 sm:gap-6 snap-x snap-mandatory">
                        {featured.map((p, idx) => (
                            <div
                                key={p.id}
                                className={`snap-start shrink-0 w-[82vw] sm:w-[420px] md:w-[380px] card-enter card-enter-${Math.min(idx % 12, 11)}`}
                            >
                                <PropertyCard property={p} />
                            </div>
                        ))}
                        {featured.length === 0 && (
                            <div className="w-full text-center text-white/50 py-12">No properties listed yet.</div>
                        )}
                    </div>
                </div>
            </section>

            {/* MOBILE-ONLY: Surface "Explore Property Services" right after property listings
                so users discover trusted pros without scrolling to the bottom. Desktop keeps
                the carousel further down in its original position.
                NOTE: Mobile now renders this inside <MobileNativeHome/>, so we no longer need
                this slot here (it was duplicating on mobile). Kept as a no-op comment for the
                desktop wrapper below. */}

            {/* POPULAR PROPERTIES — compact grid so the home page feels "full" of listings */}
            {featured.length > 0 && (
                <section className="py-10 sm:py-20 bg-[#FAFAF7]" data-testid="home-popular-grid">
                    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
                        <div className="flex items-end justify-between mb-8 sm:mb-10 flex-wrap gap-4">
                            <div>
                                <div className="label-overline mb-2">Fresh on the market</div>
                                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-500">
                                    Popular <span className="text-gold-500">properties</span> for you
                                </h2>
                                <p className="text-navy-500/65 text-sm mt-2 max-w-xl">
                                    Handpicked listings across Nagpur, Mumbai, Pune, Bengaluru and 20+ cities — updated every hour.
                                </p>
                            </div>
                            <Link
                                to="/properties"
                                data-testid="home-popular-grid-cta"
                                className="text-on-image inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy-500 text-white text-sm font-semibold hover:bg-navy-400 transition-colors"
                            >
                                View all listings <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>

                        {/* Dense tile grid — balanced for 9-18 listings */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="home-popular-tiles">
                            {featured.slice(0, 15).map((p, idx) => (
                                <Link
                                    key={p.id}
                                    to={`/properties/${p.id}`}
                                    data-testid={`popular-tile-${p.id}`}
                                    className={`group block rounded-xl overflow-hidden bg-white border border-navy-500/10 hover:border-gold-500/50 hover-lift transition-colors shadow-sm card-enter card-enter-${Math.min(idx % 12, 11)}`}
                                >
                                    <div className="relative aspect-[4/3] bg-navy-500/5 overflow-hidden">
                                        {p.images?.[0] ? (
                                            <img
                                                src={fileUrl(p.images[0])}
                                                alt={p.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-navy-500/30">
                                                <MapPin className="w-6 h-6" />
                                            </div>
                                        )}
                                        <div className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-bold uppercase tracking-widest text-navy-500 border border-navy-500/10 shadow-sm">
                                            For {p.listing_type}
                                        </div>
                                        {p.featured && (
                                            <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-gold-500 text-white text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-sm">
                                                <ShieldCheck className="w-2.5 h-2.5" /> Featured
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-2.5 sm:p-3 space-y-1">
                                        <div className="text-gold-600 font-heading font-bold text-sm sm:text-base leading-none">
                                            {formatINR(p.price)}
                                            {p.listing_type === "rent" && <span className="text-[9px] font-medium text-navy-500/60 ml-1">/mo</span>}
                                        </div>
                                        <div className="text-[11px] text-navy-500 font-semibold line-clamp-1">
                                            {p.title}
                                        </div>
                                        <div className="text-[10px] text-navy-500/60 flex items-center gap-1 line-clamp-1">
                                            <MapPin className="w-2.5 h-2.5 text-gold-600 shrink-0" />
                                            <span className="line-clamp-1">{[p.locality, p.city].filter(Boolean).join(", ")}</span>
                                        </div>
                                        {(p.bhk != null || p.area_sqft != null) ? (
                                            <div className="flex items-center gap-2 text-[10px] text-navy-500/70 pt-1 border-t border-navy-500/10 min-h-[26px]">
                                                {p.bhk != null && <span className="inline-flex items-center gap-0.5"><BedDouble className="w-2.5 h-2.5 text-gold-600" />{p.bhk}</span>}
                                                {p.bathrooms != null && <span className="inline-flex items-center gap-0.5"><Bath className="w-2.5 h-2.5 text-gold-600" />{p.bathrooms}</span>}
                                                {p.area_sqft != null && <span className="inline-flex items-center gap-0.5"><Maximize className="w-2.5 h-2.5 text-gold-600" />{p.area_sqft}sf</span>}
                                            </div>
                                        ) : (
                                            <div className="min-h-[26px] pt-1 border-t border-navy-500/10" aria-hidden="true" />
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* LUXURY PROPERTIES STRIP — only when at least 1 property crosses ₹1 Cr */}
            {luxury.length > 0 && (
                <section
                    className="text-on-image py-16 sm:py-20 relative overflow-hidden bg-gradient-to-br from-[#0a1a3a] via-[#001a3d] to-[#12234b] border-t border-gold-500/20"
                    data-testid="home-luxury"
                >
                    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(218,165,32,0.15) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(218,165,32,0.12) 0%, transparent 45%)" }} />
                    <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 mb-8 sm:mb-10">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-gold-500/15 border border-gold-500/40">
                                    <Crown className="w-3.5 h-3.5 text-gold-400" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-400">Luxury Collection</span>
                                </div>
                                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                                    Homes worth <span className="text-gold-400">₹1 Crore+</span>
                                </h2>
                                <p className="text-white/65 text-sm sm:text-base mt-3 max-w-xl">
                                    Handpicked penthouses, villas, luxury plots and premium commercial spaces from India&apos;s finest developers.
                                </p>
                            </div>
                            <Link
                                to="/properties?minPrice=10000000"
                                data-testid="home-luxury-cta"
                                className="text-on-image hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-500 text-navy-900 font-bold text-sm hover:bg-gold-100 transition-colors border border-gold-500/50"
                            >
                                View all luxury <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    <div className="relative px-5 sm:px-6 lg:px-8 overflow-x-auto pb-2 mobile-listing">
                        <div className="flex gap-4 sm:gap-6 snap-x snap-mandatory">
                            {luxury.map((p, idx) => (
                                <div
                                    key={p.id}
                                    className={`snap-start shrink-0 w-[82vw] sm:w-[420px] md:w-[380px] card-enter card-enter-${Math.min(idx % 12, 11)}`}
                                >
                                    <PropertyCard property={p} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* UPCOMING BANK AUCTIONS — drives awareness for the auction business */}
            <section className="py-10 sm:py-20 bg-navy-900 border-t border-white/5" data-testid="home-upcoming-auctions">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-[10px] uppercase tracking-[0.18em] font-bold text-gold-300">
                                    <Gavel className="w-3 h-3" /> Bank Auctions
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-[10px] uppercase tracking-[0.18em] font-bold text-red-300">
                                    <Flame className="w-3 h-3" /> Live & Upcoming
                                </span>
                            </div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white">
                                Upcoming <span className="text-gold-500">bank auction</span> properties
                            </h2>
                            <p className="text-white/55 text-sm mt-3 max-w-2xl">SARFAESI, DRT and court-ordered bank auctions across India. Place real-time bids and win below-market deals.</p>
                        </div>
                        <Link
                            to="/auctions"
                            data-testid="view-all-auctions"
                            className="text-sm font-medium text-gold-500 hover:text-gold-100 inline-flex items-center gap-1 self-start md:self-auto"
                        >
                            View all auctions <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
                {upcomingAuctions.length === 0 ? (
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="rounded-2xl border border-dashed border-gold-500/30 bg-navy-800/40 p-10 text-center">
                            <Gavel className="w-10 h-10 text-gold-500/60 mx-auto mb-3" />
                            <h3 className="font-heading text-xl font-bold text-white mb-1">No upcoming auctions right now</h3>
                            <p className="text-white/55 text-sm">Check back soon — new bank auctions are added every week.</p>
                            <Link to="/auctions" className="inline-flex items-center gap-1 text-sm text-gold-500 mt-4">Browse ended auctions <ArrowRight className="w-3.5 h-3.5" /></Link>
                        </div>
                    </div>
                ) : (
                    <div className="px-6 lg:px-8 overflow-x-auto pb-4">
                        <div className="flex gap-6 stagger snap-x snap-mandatory">
                            {upcomingAuctions.map((a) => (
                                <div key={a.id} className="snap-start shrink-0 w-[88vw] sm:w-[400px] md:w-[360px]">
                                    <AuctionTeaserCard auction={a} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* SERVICES */}
            <section className="py-24 bg-navy-900">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex items-end justify-between mb-12">
                        <div>
                            <div className="label-overline mb-3">What we do</div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white max-w-2xl">
                                Six premium services. <span className="text-gold-500">One trusted ecosystem.</span>
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                        {SERVICES.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    key={s.title}
                                    to={s.to}
                                    data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="group rounded-2xl bg-navy-800 border border-white/5 p-7 hover:border-gold-500/40 hover-lift"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-gold-500/10 border border-gold-500/30 flex items-center justify-center mb-5 group-hover:bg-gold-500 group-hover:text-navy-900 transition-colors">
                                        <Icon className="w-6 h-6 text-gold-500 group-hover:text-navy-900" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold text-white mb-2">{s.title}</h3>
                                    <p className="text-sm text-white/65 leading-relaxed mb-4">{s.desc}</p>
                                    <span className="text-xs uppercase tracking-[0.2em] text-gold-500 inline-flex items-center gap-1">
                                        Explore <ArrowRight className="w-3 h-3" />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TOP PARTNERS */}
            <section className="py-24 bg-navy-900 grid-pattern">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
                        <div>
                            <div className="label-overline mb-3">Verified Partners</div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white">
                                Top-rated <span className="text-gold-500">brokers, advocates & loan agents</span>
                            </h2>
                        </div>
                        <Link to="/brokers" className="text-sm font-medium text-gold-500 hover:text-gold-100 inline-flex items-center gap-1">
                            See all partners <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
                        {partners.map((p) => <AgentCard key={p.id} agent={p} />)}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="rounded-3xl glass p-12 lg:p-16 text-center gold-border-glow">
                        <div className="label-overline mb-4">List with us</div>
                        <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white max-w-3xl mx-auto leading-tight">
                            List your property with India&apos;s most <span className="text-gold-500">trusted ecosystem.</span>
                        </h2>
                        <p className="text-white/65 max-w-2xl mx-auto mt-5 mb-10">
                            Reach millions of verified buyers and tenants. Free first listing. No hidden fees. Verified leads only.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <Link
                                to="/register"
                                data-testid="cta-list-property"
                                className="px-8 py-4 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 transition-colors"
                            >
                                List My Property
                            </Link>
                            <Link
                                to="/pricing"
                                className="px-8 py-4 rounded-md border border-gold-500/40 text-gold-500 font-medium hover:bg-gold-500/10 transition-colors"
                            >
                                See Pricing
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* REFER & EARN — Invite friends */}
            <section className="py-10 sm:py-20" style={{ background: "linear-gradient(135deg, #FAFAF7 0%, #FFFBEF 100%)" }} data-testid="home-refer-invite">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gold-500/30 mb-4 shadow-sm">
                                <Sparkles className="w-3.5 h-3.5 text-gold-600" />
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-700">Refer & Earn</span>
                            </div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-navy-500 leading-[1.1]">
                                Invite Friends &<br /><span className="gold-shimmer">Earn Rewards.</span>
                            </h2>
                            <p className="text-navy-500/75 mt-5 max-w-xl leading-relaxed">
                                Share Global Property Mart and earn rewards for every successful signup. <strong className="text-navy-500">₹200</strong> bonus credits per signup + <strong className="text-navy-500">₹1,000</strong> per verified partner + plan bonuses up to <strong className="text-gold-700">₹700</strong>.
                            </p>
                            <div className="flex flex-wrap gap-3 mt-7">
                                <Link to="/refer" data-testid="home-cta-start-earning" className="text-on-image px-7 py-3.5 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-md inline-flex items-center gap-2">
                                    Start Earning <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/refer" data-testid="home-cta-share-website" className="text-on-image px-7 py-3.5 rounded-xl bg-navy-500 text-white font-bold text-sm hover:bg-[#1a3a6e] shadow-md">
                                    Share Website
                                </Link>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-2xl bg-white border border-navy-500/10 p-5 shadow-sm">
                                <div className="text-3xl">🎁</div>
                                <div className="font-heading text-2xl font-bold text-navy-500 mt-2">₹200</div>
                                <div className="text-xs uppercase tracking-wider text-navy-500/55 font-semibold mt-1">per signup</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-gold-500/40 p-5 shadow-md lg:scale-105">
                                <div className="text-3xl">🛡️</div>
                                <div className="font-heading text-2xl font-bold text-gold-700 mt-2">+₹1,000</div>
                                <div className="text-xs uppercase tracking-wider text-navy-500/55 font-semibold mt-1">per verified partner</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-navy-500/10 p-5 shadow-sm">
                                <div className="text-3xl">⚡</div>
                                <div className="font-heading text-2xl font-bold text-navy-500 mt-2">₹300</div>
                                <div className="text-xs uppercase tracking-wider text-navy-500/55 font-semibold mt-1">Gold plan signup</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-navy-500/10 p-5 shadow-sm">
                                <div className="text-3xl">👑</div>
                                <div className="font-heading text-2xl font-bold text-navy-500 mt-2">₹700</div>
                                <div className="text-xs uppercase tracking-wider text-navy-500/55 font-semibold mt-1">Platinum plan signup</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* REFER & EARN — Up to 1% Rewards */}
            <section className="text-on-image py-10 sm:py-20 bg-gradient-to-br from-[#002657] to-[#1a3a6e] text-white relative overflow-hidden" data-testid="home-refer-commission">
                <div aria-hidden className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(ellipse 800px 400px at 80% 50%, rgba(218,165,32,0.4), transparent 60%)" }} />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div className="rounded-3xl bg-white/8 backdrop-blur-md border border-gold-500/40 p-8 lg:p-10">
                            <div className="label-overline mb-4 text-gold-300">Tiered reward slabs</div>
                            <h3 className="font-heading text-3xl lg:text-4xl font-bold leading-tight">
                                Earn <span className="gold-shimmer">Up To 1% Rewards</span><br />
                                on every property deal.
                            </h3>
                            <div className="mt-6 space-y-2 text-sm">
                                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/75">Up to ₹10 L</span><span className="font-bold text-gold-300">₹5,000 fixed</span></div>
                                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/75">₹10 – 25 L</span><span className="font-bold text-gold-300">0.25%</span></div>
                                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/75">₹25 – 50 L</span><span className="font-bold text-gold-300">0.20%</span></div>
                                <div className="flex justify-between py-2"><span className="text-white/75">Above ₹50 L</span><span className="font-bold text-gold-300">0.10 – 0.15%</span></div>
                            </div>
                        </div>
                        <div className="lg:pl-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/40 mb-4">
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-300">Property Rewards</span>
                            </div>
                            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.1]">
                                Refer Properties &<br /><span className="gold-shimmer">Earn Up To 1% Rewards.</span>
                            </h2>
                            <p className="text-white/75 mt-5 max-w-xl leading-relaxed">
                                Refer properties and earn rewards based on successful deal value. Works on plots, flats, houses, commercial and auction listings. No cap, no hidden conditions.
                            </p>
                            <div className="flex flex-wrap gap-3 mt-7">
                                <Link to="/properties" data-testid="home-cta-share-property" className="text-on-image px-7 py-3.5 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-md inline-flex items-center gap-2">
                                    Share Property <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/refer" data-testid="home-cta-invite-now" className="text-on-image px-7 py-3.5 rounded-xl bg-white/10 border border-white/30 text-white font-bold text-sm hover:bg-white/20 backdrop-blur-md">
                                    Invite Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* RECENTLY VIEWED — only when user has viewed at least 2 */}
            {recentlyViewed.length >= 2 && (
                <section className="py-10 sm:py-20 bg-white" data-testid="home-recently-viewed">
                    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
                        <div className="flex items-end justify-between mb-6 sm:mb-10">
                            <div>
                                <div className="label-overline mb-2">Continue your search</div>
                                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-500">
                                    Recently <span className="text-gold-500">viewed</span>
                                </h2>
                            </div>
                            <Link to="/properties" className="text-sm font-semibold text-gold-600 hover:text-gold-800 hidden sm:inline">
                                Browse all →
                            </Link>
                        </div>
                    </div>
                    <div className="px-5 sm:px-6 lg:px-8 overflow-x-auto pb-4 mobile-listing">
                        <div className="flex gap-4 sm:gap-6 snap-x snap-mandatory">
                            {recentlyViewed.map((rv, idx) => (
                                <Link
                                    key={rv.id}
                                    to={`/properties/${rv.id}`}
                                    data-testid={`recent-card-${rv.id}`}
                                    className={`snap-start shrink-0 w-[70vw] sm:w-[320px] group block rounded-2xl overflow-hidden bg-white border border-navy-500/10 hover:border-gold-500/50 hover-lift transition-colors card-enter card-enter-${Math.min(idx, 11)}`}
                                >
                                    <div className="relative aspect-[16/11] bg-navy-500/5">
                                        {rv.image ? (
                                            <img src={fileUrl(rv.image)} alt={rv.title} className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-navy-500/30">
                                                <MapPin className="w-8 h-8" />
                                            </div>
                                        )}
                                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/95 text-[10px] text-navy-500 font-bold uppercase tracking-wider border border-navy-500/10">
                                            For {rv.listing_type}
                                        </div>
                                    </div>
                                    <div className="p-4 space-y-1.5">
                                        <div className="font-heading font-bold text-navy-500 text-sm line-clamp-1">{rv.title}</div>
                                        <div className="text-gold-600 font-heading font-bold text-lg">
                                            {formatINR(rv.price)}
                                            {rv.listing_type === "rent" && <span className="text-[10px] font-medium text-navy-500/60 ml-1">/mo</span>}
                                        </div>
                                        <div className="text-[11px] text-navy-500/65 flex items-center gap-1">
                                            <MapPin className="w-3 h-3 text-gold-600" />
                                            <span className="line-clamp-1">{[rv.locality, rv.city].filter(Boolean).join(", ")}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* TESTIMONIALS */}
            {testimonials.length > 0 && (
                <section className="py-16 sm:py-24 bg-[#FAFAF7]" data-testid="home-testimonials">
                    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <div className="label-overline mb-3">What our customers say</div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-500">
                                Real stories from <span className="text-gold-500">real buyers</span>
                            </h2>
                            <p className="text-navy-500/65 text-sm mt-4">Trusted by thousands of home buyers, brokers, builders and investors across India.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {testimonials.slice(0, 6).map((t) => (
                                <div
                                    key={t.id}
                                    data-testid={`testimonial-${t.id}`}
                                    className="relative rounded-2xl bg-white border border-navy-500/10 p-6 shadow-sm hover:shadow-md hover:border-gold-500/40 transition-shadow"
                                >
                                    <Quote className="w-6 h-6 text-gold-500/25 absolute top-4 right-4" />
                                    <div className="flex items-center gap-1 text-gold-500 mb-3">
                                        {[1,2,3,4,5].map((n) => (
                                            <Star key={n} className={`w-3.5 h-3.5 ${n <= t.rating ? "fill-current" : ""}`} />
                                        ))}
                                    </div>
                                    <p className="text-navy-500/85 text-sm leading-relaxed mb-5 line-clamp-5">&ldquo;{t.comment}&rdquo;</p>
                                    <div className="flex items-center gap-3 pt-4 border-t border-navy-500/10">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-amber-400 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                                            {(t.name || "?").charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="text-navy-500 font-heading font-bold text-sm">{t.name}</div>
                                            <div className="text-[11px] text-navy-500/60">
                                                {t.role}{t.city ? ` · ${t.city}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* EXPLORE PROPERTY SERVICES — desktop/tablet position. On mobile this is
                rendered near the top (right after property listings) instead. */}
            <div className="hidden md:block">
                <ServiceProfessionalsCarousel />
            </div>

            {/* INVESTMENT GUIDES */}
            <section className="py-10 sm:py-20 bg-white border-t border-navy-500/10" data-testid="home-guides">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between mb-8 sm:mb-12 flex-wrap gap-4">
                        <div>
                            <div className="label-overline mb-3 inline-flex items-center gap-2">
                                <BookOpen className="w-3.5 h-3.5" /> Investment Guides
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-500">
                                Buy smarter with <span className="text-gold-500">expert insights</span>
                            </h2>
                            <p className="text-navy-500/65 text-sm mt-3 max-w-2xl">
                                Data-backed guides on Nagpur real estate, RERA due-diligence, home loans, bank auctions and first-time buying — updated for 2026.
                            </p>
                        </div>
                        <Link
                            to="/guides"
                            className="text-on-image hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy-500 text-white text-sm font-semibold hover:bg-navy-800 transition-colors"
                            data-testid="home-guides-cta"
                        >
                            All guides <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                        {GUIDES.slice(0, 3).map((g) => (
                            <Link
                                key={g.slug}
                                to={`/guides/${g.slug}`}
                                data-testid={`home-guide-card-${g.slug}`}
                                className="group block rounded-2xl bg-white border border-navy-500/10 overflow-hidden hover:border-gold-500/50 hover-lift transition-colors"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={g.hero} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                </div>
                                <div className="p-5 space-y-3">
                                    <div className="flex items-center gap-3 text-[11px]">
                                        <span className="px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-700 font-bold uppercase tracking-wider">
                                            {g.category}
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-navy-500/60">
                                            <Clock className="w-3 h-3" /> {g.readMin} min
                                        </span>
                                    </div>
                                    <h3 className="font-heading font-bold text-navy-500 text-lg leading-snug line-clamp-2 group-hover:text-gold-700 transition-colors">
                                        {g.title}
                                    </h3>
                                    <p className="text-sm text-navy-500/70 leading-relaxed line-clamp-2">{g.excerpt}</p>
                                    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gold-600 group-hover:text-gold-800 pt-1">
                                        Read guide <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-24 bg-white" data-testid="home-faq">
                <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <div className="label-overline mb-3">Frequently asked</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-500">
                            Everything you need to <span className="text-gold-500">know</span>
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {FAQ_ITEMS.map((f, idx) => {
                            const open = openFaq === idx;
                            return (
                                <div
                                    key={idx}
                                    data-testid={`faq-${idx}`}
                                    className={`rounded-2xl border transition-colors ${
                                        open ? "border-gold-500/50 bg-[#FFFBEF]" : "border-navy-500/10 bg-white hover:border-navy-500/20"
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(open ? -1 : idx)}
                                        aria-expanded={open}
                                        className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                                    >
                                        <span className="font-heading font-bold text-navy-500 text-sm sm:text-base">{f.q}</span>
                                        <ChevronDown className={`w-5 h-5 text-gold-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                                    </button>
                                    {open && (
                                        <div className="px-5 sm:px-6 pb-5 text-navy-500/75 text-sm leading-relaxed">
                                            {f.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-10 text-center">
                        <Link
                            to="/contact"
                            data-testid="faq-contact-cta"
                            className="text-on-image inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-sm"
                        >
                            Still have questions? Contact us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
            </div>{/* /hidden md:block desktop wrapper */}
        </div>
    );
}

const FAQ_ITEMS = [
    {
        q: "How does Global Property Mart verify listings?",
        a: "Every partner (broker, advocate, loan agent, architect, interior designer, civil contractor, builder) undergoes RERA/Bar Council checks and a KYC review. Verified listings display a green shield badge across the platform. Report suspicious listings by emailing globalpropertymart95@gmail.com — our moderation team responds within 24 hours.",
    },
    {
        q: "Is it free to list a property?",
        a: "Yes — the Starter plan is completely free for the first 15 days and includes 5 active listings. Paid plans (Silver, Gold, Platinum) unlock featured placement, priority WhatsApp leads, custom branding and unlimited listings starting at ₹1,000 for 30 days.",
    },
    {
        q: "Can I buy a property that is up for bank auction?",
        a: "Absolutely. Our Auctions section shows SARFAESI/DRT/Court-ordered auctions with full transparency — borrower name, EMD amount, possession status (physical/symbolic), reserve price and inspection dates. Bid live from your dashboard; we support KYC-verified bidders only.",
    },
    {
        q: "How does the ₹200 signup bonus work?",
        a: "Every new signup gets ₹200 in bonus credits added to their wallet. These are non-withdrawable but can be used towards any subscription plan on Global Property Mart. Refer a friend and get an extra ₹200 when they sign up, plus ₹1,000 (withdrawable) when they get verified.",
    },
    {
        q: "Do you offer home loans and legal help?",
        a: "Yes. Our Loan Services page compares rates from 20+ RBI-approved lenders with an EMI calculator and eligibility checker. Our verified Advocates panel handles title checks, agreement drafting, registration support and property disputes — book a paid consultation from any advocate profile.",
    },
    {
        q: "Which cities and areas do you cover?",
        a: "Global Property Mart is headquartered in Nagpur and covers all of Maharashtra deeply — Wardha Road, MIHAN, Civil Lines, Mohgaon, Beltarodi, Jamtha and 30+ Nagpur localities. We also serve buyers/sellers in Mumbai, Pune, Bengaluru, Hyderabad, Delhi-NCR and 20+ other Indian cities.",
    },
];


/* -------------------- AUCTION TEASER CARD (Home page) -------------------- */
function AuctionTeaserCard({ auction: a }) {
    const isLive = a.status === "live";
    const isUpcoming = a.status === "upcoming";
    const target = isUpcoming ? a.start_time : a.end_time;
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = new Date(target).getTime() - now;
    let countdown = "Ended";
    if (diff > 0) {
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        countdown = d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
    }
    const img = a.images?.[0]
        ? (typeof a.images[0] === "string" && a.images[0].startsWith("http")
            ? a.images[0]
            : fileUrl(a.images[0]))
        : "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85";

    return (
        <Link
            to={`/auctions/${a.id}`}
            data-testid={`home-auction-card-${a.id}`}
            className="group block rounded-2xl overflow-hidden bg-navy-800 border border-white/5 hover:border-gold-500/40 transition-colors h-full"
        >
            <div className="relative aspect-[16/10] overflow-hidden">
                <img src={img} alt={a.borrower_name || a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 via-transparent" />
                {isLive && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-white" /> Live
                    </div>
                )}
                {isUpcoming && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gold-500 text-navy-900 text-[10px] font-bold uppercase tracking-widest">
                        Upcoming
                    </div>
                )}
                {a.auction_type && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider">
                        {a.auction_type}
                    </div>
                )}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                    <div className="flex items-center gap-1.5 text-xs font-mono bg-black/50 backdrop-blur-sm rounded px-2 py-1">
                        <Clock className="w-3 h-3 text-gold-400" />
                        {isUpcoming ? `Starts in ${countdown}` : `${countdown} left`}
                    </div>
                </div>
            </div>
            <div className="p-5 space-y-2.5">
                {a.borrower_name && (
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.18em] text-white/45 font-bold">Borrower</div>
                        <div className="text-white font-heading font-bold text-base leading-tight line-clamp-1">{a.borrower_name}</div>
                    </div>
                )}
                {a.bank_name && (
                    <div className="flex items-center gap-1.5 text-xs text-white/75">
                        <Building2 className="w-3.5 h-3.5 text-gold-500 shrink-0" />
                        <span className="truncate">{a.bank_name}{a.branch ? ` · ${a.branch}` : ""}</span>
                    </div>
                )}
                <div className="flex items-center gap-1.5 text-xs text-white/60">
                    <MapPin className="w-3.5 h-3.5 text-gold-500 shrink-0" />
                    <span className="truncate">{a.locality ? `${a.locality}, ` : ""}{a.city}</span>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-white/5 text-[11px] text-white/70 flex-wrap">
                    {a.possession_type && (
                        <span className="flex items-center gap-1">
                            <KeyRound className="w-3.5 h-3.5 text-gold-500" />
                            {a.possession_type === "physical" ? "Physical" : "Symbolic"}
                        </span>
                    )}
                    {a.emd_amount && (
                        <span className="flex items-center gap-1">
                            <IndianRupee className="w-3.5 h-3.5 text-gold-500" />
                            EMD {formatINR(a.emd_amount)}
                        </span>
                    )}
                </div>
                <div className="flex items-end justify-between pt-2 border-t border-white/5">
                    <div>
                        <div className="text-[9px] uppercase tracking-wider text-white/45">Base Price</div>
                        <div className="font-heading text-lg font-bold text-gold-500">{formatINR(a.base_price || a.current_price)}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/45 text-right">
                        View<br/>Details
                    </div>
                </div>
            </div>
        </Link>
    );
}
